import React, { useEffect, useRef } from "react";
import { Surface } from "../../../../atoms/Surface";
import { AlertMessage } from "../../../../atoms/AlertMessage";
import {
  HiInformationCircle,
  HiCheckCircle,
  HiArrowRight,
  HiLockClosed,
} from "react-icons/hi";
import { Controller, UseFormSetValue } from "react-hook-form";
import { Radio } from "flowbite-react";
import { useTranslation } from "react-i18next";
import { CustomInput } from "../../../../atoms/CustomInput";
import { UnlockBadge } from "../../../../atoms/UnlockBadge";
import { RegistrationFormData } from "../types";
import { CharacterCounter } from "../../../../atoms/CharacterCounter";

type Props = {
  control: any;
  watch: (name: string) => any;
  errors: any;
  setValue?: UseFormSetValue<RegistrationFormData>;
  hasRedirectUrlAccess?: boolean;
  onUpgradeClick?: () => void;
  readOnly?: boolean;
};

export const PostRegistrationActionsSection: React.FC<Props> = ({
  control,
  watch,
  errors,
  setValue,
  hasRedirectUrlAccess = true,
  onUpgradeClick,
  readOnly = false,
}) => {
  const { t } = useTranslation();
  const postRegistrationAction = watch("postRegistrationAction");
  const redirectUrl = watch("redirectUrl");

  const savedRedirectUrlRef = useRef<string>("");

  useEffect(() => {
    if (redirectUrl && redirectUrl.trim() !== "") {
      savedRedirectUrlRef.current = redirectUrl;
    }
  }, [redirectUrl]);

  useEffect(() => {
    if (
      postRegistrationAction === "redirect" &&
      setValue &&
      savedRedirectUrlRef.current
    ) {
      const currentUrl = redirectUrl || "";
      if (
        currentUrl.trim() === "" &&
        savedRedirectUrlRef.current.trim() !== ""
      ) {
        setValue("redirectUrl", savedRedirectUrlRef.current, {
          shouldValidate: false,
          shouldDirty: false,
        });
      }
    }
  }, [postRegistrationAction, setValue, redirectUrl]);

  return (
    <Surface>
      <div className="mb-4">
        <p className="text-gray-500 text-sm leading-tight">
          {t("customRegistration.postActions.description")}
        </p>
      </div>

      <div className="space-y-3">
        <Controller
          name="postRegistrationAction"
          control={control}
          render={({ field }) => (
            <div
              onClick={readOnly ? undefined : () => field.onChange("toast")}
              className={`flex flex-col px-3 py-3 rounded transition-all duration-200 ${
                readOnly ? "opacity-60 cursor-not-allowed" : "cursor-pointer"
              } ${
                field.value === "toast"
                  ? "bg-blue-50 border-2 border-blue-500 shadow-sm"
                  : readOnly
                  ? "bg-white border border-gray-200"
                  : "bg-white border border-gray-200 hover:border-blue-300"
              }`}
            >
              <div className="flex items-start gap-3">
                <div
                  className={`flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center transition-colors ${
                    field.value === "toast"
                      ? "bg-blue-500 text-white"
                      : "bg-gray-100 text-gray-500"
                  }`}
                >
                  <HiCheckCircle size={20} />
                </div>

                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <Radio
                      id="action-toast"
                      value="toast"
                      checked={field.value === "toast"}
                      onChange={(e) => field.onChange(e.target.value)}
                      className="mt-0.5"
                    />
                    <label
                      htmlFor="action-toast"
                      className="text-gray-900 text-sm font-semibold cursor-pointer"
                    >
                      {t("customRegistration.postActions.toastOption")}
                    </label>
                  </div>
                </div>

                {field.value === "toast" && (
                  <div className="flex-shrink-0">
                    <HiCheckCircle size={20} className="text-blue-500" />
                  </div>
                )}
              </div>
            </div>
          )}
        />

        {hasRedirectUrlAccess ? (
          <Controller
            name="postRegistrationAction"
            control={control}
            render={({ field }) => (
              <div
                onClick={
                  readOnly ? undefined : () => field.onChange("redirect")
                }
                className={`flex flex-col px-3 py-3 rounded transition-all duration-200 ${
                  readOnly ? "opacity-60 cursor-not-allowed" : "cursor-pointer"
                } ${
                  field.value === "redirect"
                    ? "bg-blue-50 border-2 border-blue-500 shadow-sm"
                    : readOnly
                    ? "bg-white border border-gray-200"
                    : "bg-white border border-gray-200 hover:border-blue-300"
                }`}
              >
                <div className="flex items-start gap-3">
                  <div
                    className={`flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center transition-colors ${
                      field.value === "redirect"
                        ? "bg-blue-500 text-white"
                        : "bg-gray-100 text-gray-500"
                    }`}
                  >
                    <HiArrowRight size={20} />
                  </div>

                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <Radio
                        id="action-redirect"
                        value="redirect"
                        checked={field.value === "redirect"}
                        onChange={(e) => field.onChange(e.target.value)}
                        className="mt-0.5"
                      />
                      <label
                        htmlFor="action-redirect"
                        className="text-gray-900 text-sm font-semibold cursor-pointer"
                      >
                        {t("customRegistration.postActions.redirectOption")}
                      </label>
                    </div>
                  </div>

                  {field.value === "redirect" && (
                    <div className="flex-shrink-0">
                      <HiCheckCircle size={20} className="text-blue-500" />
                    </div>
                  )}
                </div>

                {field.value === "redirect" && (
                  <div
                    className="mt-3 sm:ml-[52px] animate-slideIn"
                    translate="no"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <CustomInput
                      name="redirectUrl"
                      control={control}
                      label={
                        t(
                          "customRegistration.postActions.redirectUrlLabel"
                        ) as string
                      }
                      placeholder={
                        t(
                          "customRegistration.postActions.redirectUrlPlaceholder"
                        ) as string
                      }
                      type="url"
                      error={errors.redirectUrl}
                      helperText={errors.redirectUrl}
                      maxLength={300}
                    />
                    <CharacterCounter
                      currentLength={watch("redirectUrl")?.length || 0}
                      maxLength={300}
                    />
                  </div>
                )}
              </div>
            )}
          />
        ) : (
          <div className="flex flex-col px-3 py-3 rounded bg-gray-50 border border-gray-200">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center bg-gray-200 text-gray-400">
                <HiLockClosed size={20} />
              </div>

              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-gray-500 text-sm font-semibold">
                    {t("customRegistration.postActions.redirectOption")}
                  </span>
                  <UnlockBadge onClick={onUpgradeClick} />
                </div>
                <p className="text-gray-400 text-xs mt-1">
                  {t("customRegistration.postActions.redirectUpgradeHint")}
                </p>
              </div>
            </div>
          </div>
        )}

        {errors.postRegistrationAction && (
          <AlertMessage
            color="failure"
            icon={HiInformationCircle}
            className="mt-2"
          >
            {errors.postRegistrationAction.message}
          </AlertMessage>
        )}
      </div>
    </Surface>
  );
};
